import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import IconTextBlock from '@component/common/block/icon-text-block';
import * as size from '@constant/size';
import { loadKakaoMaps, type IKakaoMap, type IKakaoMaps } from '@util/kakao-map';
import * as color from '@constant/color';

interface IProps {
  latitude: number;
  longitude: number;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const MapContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const ControlContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StatusContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.GRAY_4};
`;

const MapPreview: React.FC<IProps> = ({ latitude, longitude }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<IKakaoMap | null>(null);
  const mapsRef = useRef<IKakaoMaps | null>(null);
  const [satellite, setSatellite] = useState(true);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = mapRef.current;
    if (!element) return;
    let active = true;
    let cleanup = () => {};
    setSatellite(true);
    setReady(false);
    setFailed(false);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
      setFailed(true);
      return;
    }
    void loadKakaoMaps().then((maps) => {
      if (!active) return;
      const center = new maps.LatLng(latitude, longitude);
      const map = new maps.Map(element, { center, level: 4, draggable: true, scrollwheel: true });
      map.setMapTypeId(maps.MapTypeId.HYBRID);
      mapInstanceRef.current = map;
      mapsRef.current = maps;
      map.addControl(new maps.ZoomControl(), maps.ControlPosition.RIGHT);
      const marker = new maps.Marker({ position: center, map, draggable: false });
      const observer = new ResizeObserver(() => map.relayout());
      observer.observe(element);
      cleanup = () => {
        mapInstanceRef.current = null;
        mapsRef.current = null;
        observer.disconnect();
        marker.setMap(null);
        element.replaceChildren();
      };
      setReady(true);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
      cleanup();
    };
  }, [latitude, longitude]);

  const handleMapTypeChange = () => {
    const map = mapInstanceRef.current;
    const maps = mapsRef.current;
    if (!ready || failed || !map || !maps) return;
    map.setMapTypeId(satellite ? maps.MapTypeId.ROADMAP : maps.MapTypeId.HYBRID);
    setSatellite(!satellite);
  };

  return (
    <Wrapper>
      <ControlContainer>
        <IconTextBlock
          icon={satellite ? 'map' : 'satellite'}
          text={satellite ? '일반지도' : '위성지도'}
          design={{
            box: {
              gap: '5px',
              padding: '2px 5px',
              border: false,
              borderWidth: size.BORDER_WIDTH_1,
              borderColor: color.BLACK_2,
              borderRadius: size.BORDER_RADIUS_1,
              event: { cursor: 'pointer' },
            },
            text: { size: 'small', color: ready ? color.BLACK_2 : color.GRAY_2 },
            icon: { size: 'small', color: ready ? color.BLACK_2 : color.GRAY_2 },
          }}
          click={{ handler: handleMapTypeChange }}
        />
      </ControlContainer>
      <Gap height={size.MARGIN_1} />
      <MapContainer ref={mapRef} />
      {(!ready || failed) && (
        <StatusContainer>
          <TextBlock
            text={failed ? '지도를 불러오지 못했어요' : '지도 로딩 중'}
            design={{ text: { size: 'medium', color: color.GRAY_1 } }}
          />
        </StatusContainer>
      )}
    </Wrapper>
  );
};

export default MapPreview;
